import {
  ASPECT_RATIO,
  AUDIO_TRANSLATION_LANGUAGES_OPTIONS,
  CONTENT_TYPES,
  EVENT_NAME,
  NEWS_CATEGORY,
  SYNCING_STATUS,
} from "constants/phase";
import { ILLUSTO_CLIP_STATUS } from "constants/studio";
import { addDownloadDispositionToUrl, isClipHasMetadata } from "helpers";
import moment from "moment";
import { createSelector } from "reselect";
import { selectAppAdditionalPermissionsTimeStampFormatBySelectedUserId } from "store/entityConfig/selectors";

import { clipEntityAdapter, clipSlice } from "./slice";

const selectClipState = (state) => state[clipSlice.name];

export const selectAudioTrackVideoUrl = createSelector(selectClipState, (clipState) => clipState.audioTrackVideoUrl);

export const selectClipModals = createSelector(selectClipState, (clipState) => clipState.modals);

export const selectClipEventIds = createSelector(selectClipState, (clipState) => clipState.eventIds);

export const selectClipIds = createSelector(selectClipState, (clipState) =>
  clipEntityAdapter.getSelectors().selectIds(clipState),
);

export const selectFinalClipIds = createSelector(selectClipState, (clipState) => clipState.finalClipIds);

export const selectFirstClipId = createSelector(selectClipIds, (clipIds) => clipIds?.[0]);

export const selectClipEntities = createSelector(selectClipState, (clipState) =>
  clipEntityAdapter.getSelectors().selectEntities(clipState),
);

export const selectClips = createSelector(selectClipEntities, (clipEntities) =>
  Object.values(clipEntities).filter((clip) => clip?.episodeTitle && clip.episodeTitle !== ""),
);

export const selectNotRetainedClipIds = createSelector(selectClipEntities, (clipEntities) =>
  Object.values(clipEntities)
    .filter((clip) => !clip?.retainedClip && !clip?.hasOwnProperty("folderRating") && !clip?.isHighlightClip)
    .map((clip) => clip._id),
);

export const selectClipsEligibleForAudioCutCheck = createSelector(
  selectNotRetainedClipIds,
  selectClipEntities,
  (notRetainedClipIds, clipEntities) =>
    Object.values(clipEntities)
      .filter((clip) => notRetainedClipIds.includes(clip._id))
      .reduce((acc, clip) => {
        if (!clip.isPromoVideo && !clip.isClipCreateByIllusto && !clip.isHighlightVideo) {
          acc.push(clip);
        }

        return acc;
      }, []),
);

export const selectCurrentPageClipCount = createSelector(selectClipState, (clipState) =>
  clipEntityAdapter.getSelectors().selectTotal(clipState),
);

export const selectSelectedClips = createSelector(selectClipState, (clipState) => clipState.selectedClips);

export const selectSelectedClipsDetails = createSelector(
  selectClipState,
  (clipState) => clipState.selectedClipsDetails,
);

export const selectSelectedClipsVideoUrls = createSelector(
  [selectClipEntities, selectSelectedClips],
  (clipEntities, selectedClipIds) => selectedClipIds.map((clipId) => clipEntities[clipId]?.videoUrl),
);

export const selectClipDetailsById = createSelector(
  selectClipState,
  (state, props) => ({
    clipId: props.clipId,
    clipData: props.clipData,
    contentId: props.contentId,
    contentType: props.contentType,
  }),
  (clipState, { clipId, clipData, contentId, contentType }) => {
    if (!clipId && contentId && contentType === CONTENT_TYPES.CLIP) {
      clipId = contentId;
    }

    return clipData || clipEntityAdapter.getSelectors().selectById(clipState, clipId) || null;
  },
);

export const selectClipSubtitleTracksById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.tracks?.filter((track) => track?.kind === "subtitles") || [],
);

export const selectClipAudioTracksById = createSelector(selectClipDetailsById, (clipDetails) => {
  const audioTracks = clipDetails?.tracks?.filter((track) => track?.kind === "video") || [];

  const defaultOption = {
    kind: "video",
    src: "",
    srcLang: "default",
  };

  return [defaultOption, ...audioTracks];
});

export const selectPromoClipIds = createSelector(selectClipEntities, (clipEntities) =>
  Object.values(clipEntities).reduce((acc, clip) => {
    if (clip?.isPromoVideo) {
      acc.push(clip._id);
    }

    return acc;
  }, []),
);

export const selectClipIsPromoVideo = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.isPromoVideo);

export const selectCurrentClipIndex = createSelector(selectClipState, selectClipDetailsById, (clipState, clipDetails) =>
  clipState?.ids.indexOf(clipDetails?._id),
);

export const selectClipIsHighlightVideo = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.isHighlightVideo,
);

export const selectClipTitleById = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.title);

export const selectClipStockMediaById = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.stockMedia);

export const selectClipEntityId = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.entityId);

export const selectClipEditClipFilterObjectById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.editClipFilterObject,
);

export const selectClipIsAddedToClipsPageById = createSelector(
  selectClipEditClipFilterObjectById,
  (editClipFilterObject) => editClipFilterObject?.isAddedToClipsPage,
);

export const selectClipCreatedAtById = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.createdAt);

export const selectClipId = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.id);

export const selectClipEpisodeTitleById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.episodeTitle,
);

export const selectClipDisplayTitleById = createSelector(
  selectClipTitleById,
  selectClipEpisodeTitleById,
  (title, episodeTitle) => episodeTitle || title,
);

export const selectClipAspectRatioById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.aspectRatio,
);

export const selectClipIsCroppableAspectRatioById = createSelector(
  selectClipAspectRatioById,
  (aspectRatio) => aspectRatio === ASPECT_RATIO["16_9"],
);

export const selectClipDurationById = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.duration);

export const selectClipPropertiesById = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.properties);

export const selectClipListIdArrById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.cliplistIdArr,
);

export const selectClipHighlightIdById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.highlightId,
);

export const selectClipIdArrById = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.clipIdArr);

export const selectClipEditedPreviewUrlById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.editedPreviewUrl,
);

export const selectClipDurationWithoutBumperById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.totalDurationWithoutBumper,
);

export const selectClipCustomDataById = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.customData);

export const selectClipDataById = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.clipData);

export const selectClipVideoUrlById = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.videoUrl);

export const selectClipMainVideoUrlById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.mainVideoUrl,
);

export const selectClipStreamIdById = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.streamId);

export const selectClipEditedVideosById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.editedVideos || [],
);

export const selectAutoFlipOrStaticClipEditedVideosWithCompletedStatusById = createSelector(
  selectClipDetailsById,
  (clipDetails) => {
    const autoFlipOrStaticClipEditedVideosWithCompletedStatus = [];

    clipDetails?.editedVideos?.forEach((video) => {
      const isCompletedAndValidEvent =
        video?.video_url && (video?.event === EVENT_NAME.AUTO_FLIP || video?.event === EVENT_NAME.STATIC);

      if (isCompletedAndValidEvent) {
        const existingIndex = autoFlipOrStaticClipEditedVideosWithCompletedStatus.findIndex(
          (existingVideo) => existingVideo.aspect_ratio === video.aspect_ratio,
        );

        if (existingIndex === -1) {
          // No existing video with this aspect ratio, add the new one
          autoFlipOrStaticClipEditedVideosWithCompletedStatus.push(video);
        } else {
          const existingVideo = autoFlipOrStaticClipEditedVideosWithCompletedStatus[existingIndex];
          // Replace if existing is croppedVideos and new is AUTO_FLIP
          if (existingVideo.event === EVENT_NAME.STATIC && video.event === EVENT_NAME.AUTO_FLIP) {
            autoFlipOrStaticClipEditedVideosWithCompletedStatus[existingIndex] = video;
          }
        }
      }
    });

    return autoFlipOrStaticClipEditedVideosWithCompletedStatus;
  },
);

export const selectClipTlmMarkerById = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.tlmMarker);

export const selectClipVideoThumbnailUrlById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.videoThumbnailUrl || clipDetails?.thumbnail,
);

export const selectClipVideoThumbnailUrlsById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.videoThumbnailUrls,
);

export const selectClipDisplayAspectRatio = createSelector(
  (state, props) => props.aspectRatio,
  selectClipAspectRatioById,
  (aspectRatio, clipAspectRatio) => aspectRatio || clipAspectRatio,
);

export const selectSocialMediaShareVideoUrl = createSelector(
  (state, props) => props.aspectRatio,
  selectClipVideoUrlById,
  selectClipEditedVideosById,
  (aspectRatio, clipVideoUrl, editedClipVideos) => {
    if (editedClipVideos) {
      const url = editedClipVideos.find(
        (video) => video?.aspect_ratio === aspectRatio && video.event !== EVENT_NAME.DYNAMIC,
      )?.video_url;

      return url || clipVideoUrl;
    }

    return clipVideoUrl;
  },
);

export const selectClipIsStatusCheckDisabled = createSelector(
  (state, props) => props.isStatusCheckDisabled,
  (isStatusCheckDisabled) => isStatusCheckDisabled || false,
);

export const selectClipIsDynamicClips = createSelector(
  (state, props) => props.isDynamicClips,
  (isDynamicClips) => isDynamicClips || false,
);

export const selectClipDynamicClipDocumentId = createSelector(
  (state, props) => props.documentId,
  (documentId) => documentId || null,
);

export const selectClipDetailsByAspectRatio = createSelector(
  selectClipVideoUrlById,
  selectClipVideoThumbnailUrlById,
  selectClipVideoThumbnailUrlsById,
  selectClipEditedVideosById,
  selectClipAspectRatioById,
  selectClipDisplayAspectRatio,
  selectClipIsStatusCheckDisabled,
  selectClipIsDynamicClips,
  selectClipDynamicClipDocumentId,
  (_, props) => props?.includeDynamicClips,
  (
    clipVideoUrl,
    clipVideoThumbnailUrlById,
    clipVideoThumbnailUrlsById,
    editedVideos,
    clipAspectRatio,
    clipDisplayAspectRatio,
    isStatusCheckDisabled,
    isDynamicClips,
    dynamicClipDocumentId,
    includeDynamicClips,
  ) => {
    if (isDynamicClips && !!dynamicClipDocumentId) {
      return editedVideos?.find(
        ({ documentId, event }) => documentId === dynamicClipDocumentId && event === EVENT_NAME.DYNAMIC,
      );
    }

    if (clipDisplayAspectRatio === clipAspectRatio) {
      return {
        video_url: clipVideoUrl,
        thumbnailUrl: clipVideoThumbnailUrlById,
        thumbnails: clipVideoThumbnailUrlsById || [],
      };
    }

    if (isStatusCheckDisabled) {
      return editedVideos?.find(
        ({ aspect_ratio, event }) => aspect_ratio === clipDisplayAspectRatio && event !== EVENT_NAME.DYNAMIC,
      );
    }

    return editedVideos?.find(({ aspect_ratio, status, event }) => {
      const baseCondition = aspect_ratio === clipDisplayAspectRatio && status !== SYNCING_STATUS.PROCESSING;

      return includeDynamicClips ? baseCondition : baseCondition && event !== EVENT_NAME.DYNAMIC;
    });
  },
);

export const selectClipVideoUrlByAspectRatio = createSelector(
  selectClipDetailsByAspectRatio,
  selectAudioTrackVideoUrl,
  (clipDetailsByAspectRatio, audioTrackVideoUrl) => audioTrackVideoUrl || clipDetailsByAspectRatio?.video_url,
);

export const selectClipThumbnailUrlByAspectRatio = createSelector(
  selectClipDetailsByAspectRatio,
  (clipDetailsByAspectRatio) => clipDetailsByAspectRatio?.thumbnailUrl,
);

export const selectClipVideoUrlWithDispositionByAspectRatio = createSelector(
  selectClipVideoUrlByAspectRatio,
  selectClipDisplayTitleById,
  (clipVideoUrl, clipTitle) => addDownloadDispositionToUrl(clipVideoUrl, clipTitle),
);

export const selectClipThumbnailUrlWithDispositionByAspectRatio = createSelector(
  selectClipThumbnailUrlByAspectRatio,
  selectClipDisplayTitleById,
  (clipThumbnailUrl, clipTitle) => addDownloadDispositionToUrl(clipThumbnailUrl, clipTitle),
);

export const selectClipStatusByAspectRatio = createSelector(
  selectClipDetailsByAspectRatio,
  (clipDetailsByAspectRatio) => clipDetailsByAspectRatio?.status,
);

export const selectClipDurationByAspectRatio = createSelector(
  selectClipDetailsByAspectRatio,
  (clipDetailsByAspectRatio) => clipDetailsByAspectRatio?.duration,
);

export const selectClipDynamicClips = createSelector(selectClipEditedVideosById, (editedVideos) =>
  editedVideos?.filter((video) => video.event === "dynamicCropped"),
);

export const selectClipEditedClipDataById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.editedClipData,
);

export const selectClipEditedClipDataIsPreviewById = createSelector(
  selectClipEditedClipDataById,
  (editedClipData) => editedClipData?.isPreview,
);

export const selectClipEditedClipDataPreviewUrlById = createSelector(
  selectClipEditedClipDataById,
  (editedClipData) => editedClipData?.previewUrl,
);

export const selectClipEditedClipDataAspectRatioById = createSelector(
  selectClipEditedClipDataById,
  (editedClipData) => editedClipData?.aspect_ratio,
);

export const selectClipEditedClipDataDurationById = createSelector(
  selectClipEditedClipDataById,
  (editedClipData) => editedClipData?.duration,
);

export const selectClipGeneratedThumbnailUrlsById = createSelector(selectClipDetailsById, (clipDetails) => [
  ...(clipDetails?.generatedThumbnailUrls || []).map(({ thumbnails }) => thumbnails).flat(),
]);

export const selectClipDetailsByClipIds = createSelector(
  selectClipState,
  (state, props) => props.clipIds,
  (clipState, clipIds) => {
    const validClipIds = Array.isArray(clipIds) ? clipIds.filter((id) => id != null) : [];

    return validClipIds.map((clipId) => clipEntityAdapter.getSelectors().selectById(clipState, clipId));
  },
);

export const selectClipsVideoUrls = createSelector(selectClipDetailsByClipIds, (clipDetailsByClipIds) =>
  clipDetailsByClipIds.map((clipDetails) => clipDetails?.videoUrl),
);

export const selectClipsEntityId = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.entityId);

export const selectClipStartTime = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.start_time);

export const selectClipEndTime = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.end_time);

export const selectClipCount = createSelector(selectClipState, (clipState) => clipState.count);

export const selectPromoClipCount = createSelector(selectClipState, (clipState) => clipState.promoCount);

export const selectPublishedClipCount = createSelector(selectClipState, (clipState) => clipState.publishedCount);

export const selectClipFilterCounts = createSelector(selectClipState, (clipState) => clipState.filterCounts);

export const selectClipAllFiltersModal = createSelector(selectClipModals, (modals) => modals.clipAllFiltersModal);

export const selectClipAlertModal = createSelector(selectClipModals, (modals) => modals.clipAlertModal);

export const selectIsManualClip = createSelector(selectClipDetailsById, (clipDetails) => !!clipDetails?.isManual);

export const selectIsDatafeedClip = createSelector(selectClipDetailsById, (clipDetails) =>
  Boolean(clipDetails?.datafeed && clipDetails?.datafeed?.type === "datafeed"),
);

export const selectClipRating = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.clipRating);
export const selectClipReasonForRating = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.reasonForRating,
);

export const selectClipBulkDeleteModal = createSelector(selectClipModals, (modals) => modals.clipBulkDeleteModal);

export const selectClipDeleteConvertVideoModal = createSelector(
  selectClipModals,
  (modals) => modals.clipDeleteConvertVideoModal,
);

export const selectClipCropperModal = createSelector(selectClipModals, (modals) => modals.clipCropperModal);

export const selectClipCopyToStreamModal = createSelector(selectClipModals, (modals) => modals.clipCopyToStreamModal);

export const selectClipCopyToChildModal = createSelector(selectClipModals, (modals) => modals.clipCopyToChildModal);

export const selectClipCloudPublishModal = createSelector(selectClipModals, (modals) => modals.clipCloudPublishModal);

export const selectClipCloudEditPublishModal = createSelector(
  selectClipModals,
  (modals) => modals.clipCloudEditPublishModal,
);

export const selectClipBulkDownloadModal = createSelector(selectClipModals, (modals) => modals.clipBulkDownloadModal);

export const selectClipTrimModal = createSelector(selectClipModals, (modals) => modals.clipTrimModal);

export const selectClipPreviewModal = createSelector(selectClipModals, (modals) => modals.clipPreviewModal);

export const selectUploadClipsModal = createSelector(selectClipModals, (modals) => modals.uploadClipsModal);

export const selectUploadMultiClipsModal = createSelector(selectClipModals, (modals) => modals.uploadMultiClipsModal);

export const selectmovetoClipsPageModal = createSelector(selectClipModals, (modals) => modals.movetoClipsPageModal);

export const selectClipChangeThumbnailModal = createSelector(
  selectClipModals,
  (modals) => modals.clipChangeThumbnailModal,
);

export const selectClipSocialMediaPublishModal = createSelector(
  selectClipModals,
  (modals) => modals.clipSocialMediaPublishModal,
);

export const selectClipManageAndPreviewModal = createSelector(
  selectClipModals,
  (modals) => modals.clipManageAndPreviewModal,
);

export const selectClipEnhancedTaggingModal = createSelector(
  selectClipModals,
  (modals) => modals.clipEnhancedTaggingModal,
);

export const selectClipCDTeamCustomEditModal = createSelector(
  selectClipModals,
  (modals) => modals.clipCDTeamCustomEditModal,
);

export const selectClipRatingOverwriteWarningModal = createSelector(
  selectClipModals,
  (modals) => modals.clipRatingOverwriteWarningModal,
);

export const selectClipDeleteEditedClipModal = createSelector(
  selectClipModals,
  (modals) => modals.clipDeleteEditedClipModal,
);

export const selectClipOverwriteEditedClipModal = createSelector(
  selectClipModals,
  (modals) => modals.clipOverwriteEditedClipModal,
);

export const showClipCroppedClipPreviewModal = createSelector(
  selectClipModals,
  (modals) => modals.clipCroppedClipPreviewModal,
);

export const selectClipMetadataModal = createSelector(selectClipModals, (modals) => modals.clipMetadataModal);

export const selectClipFtpPublishModal = createSelector(selectClipModals, (modals) => modals.clipFtpPublishModal);

export const selectClipSftpPublishModal = createSelector(selectClipModals, (modals) => modals.clipSftpPublishModal);

export const selectClipCMSPublishModal = createSelector(selectClipModals, (modals) => modals.clipCMSPublishModal);

export const selectClipEditorTrimClipConfirmationModal = createSelector(
  selectClipModals,
  (modals) => modals.clipEditorTrimClipConfirmationModal,
);

export const selectClipExportTemplatesModal = createSelector(
  selectClipModals,
  (modals) => modals.clipExportTemplatesModal,
);

export const selectClipAudioTranslationModal = createSelector(
  selectClipModals,
  (modals) => modals.clipAudioTranslationModal,
);
export const selectDragDropHLPreviewModal = createSelector(selectClipModals, (modals) => modals.dragDropHLPreviewModal);

export const selectClipReportIssueModal = createSelector(selectClipModals, (modals) => modals.clipReportIssueModal);

export const selectClipActualDurationById = createSelector(selectClipTlmMarkerById, (clipTlmMarket) => {
  if (clipTlmMarket) {
    return moment.utc(clipTlmMarket * 1000).format("HH:mm:ss");
  }

  return null;
});

export const selectClipIdsByDateId = createSelector(
  selectClipState,
  (state, props) => props.clipDateId,
  (clipState, clipDateId) => clipState?.groupByDate[clipDateId]?.clips,
);

export const selectGroupByDateClipIds = createSelector(selectClipState, (clipState) =>
  Object.keys(clipState?.groupByDate),
);

export const selectIllustoProjectUrl = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.illustoProjectUrl,
);
export const selectClipType = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.type);
export const selectIllustoClipStatus = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.illustoClipStatus,
);
export const selectClipIdsCount = createSelector(selectClipIds, (clipIds) => clipIds?.length);

export const selectClipProjectId = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.projectId);

export const selectClipAutoTranscriptApiById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.audioTranscript_api,
);

export const selectClipSegmentsFromTranscriptApiById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.audioTranscript_api?.segments || [],
);

export const selectClipOutcomeById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.clipData?.outcome,
);

export const selectRecentEditsClipIds = createSelector(selectClipState, (clipState) => {
  let recentEditIds = [];
  // get the clip ids of all recent edits via their date ids

  Object.keys(clipState?.groupByDate).forEach((dateId) => {
    const clipIdsOfThisDate = clipState?.groupByDate[dateId]?.clips || [];

    recentEditIds = [...recentEditIds, ...clipIdsOfThisDate];
  });

  return recentEditIds;
});

export const selectClipPublishedPlatformsById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.clipPublished,
);

export const selectWebStoriesAllFiltersModal = createSelector(
  selectClipModals,
  (modals) => modals.webStoriesAllFiltersModal,
);

export const selectPublishOnedriveModal = createSelector(selectClipModals, (modals) => modals.publishOnedriveModal);

export const selectClipIsProjectOpen = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.isProjectOpen,
);

export const selectClipFormattedCreatedAt = createSelector(
  selectClipCreatedAtById,
  selectAppAdditionalPermissionsTimeStampFormatBySelectedUserId,
  (createdAt, appAdditionalPermissionsTimeStampFormatBySelectedUserId) =>
    moment(createdAt).format(appAdditionalPermissionsTimeStampFormatBySelectedUserId),
);

export const selectClipTrimStartTime = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.trim_start_time,
);

export const selectClipTrimEndTime = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.trim_end_time);

export const selectClipDisplayStartTime = createSelector(
  selectClipTrimStartTime,
  selectClipStartTime,
  (trimStartTime, startTime) =>
    moment()
      .startOf("day")
      .seconds(parseInt(trimStartTime || startTime))
      .format("H:mm:ss"),
);

export const selectClipDisplayEndTime = createSelector(
  selectClipTrimEndTime,
  selectClipEndTime,
  (trimEndTime, endTime) =>
    moment()
      .startOf("day")
      .seconds(parseInt(trimEndTime || endTime))
      .format("H:mm:ss"),
);

export const selectClipMp4VideoUrl = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.videoUrl);

export const selectClipFolders = createSelector(selectClipState, (clipState) => clipState.clipFolders);

export const selectClipSyncingInProgress = createSelector(
  selectClipEditedVideosById,
  selectClipAspectRatioById,
  (editedVideos, aspectRatio) => {
    if (aspectRatio === ASPECT_RATIO["16_9"]) {
      return false;
    }

    return editedVideos.some(
      (editedVideos) =>
        editedVideos.length > 0 &&
        editedVideos.some(
          ({ aspect_ratio, status, event }) =>
            aspect_ratio === aspectRatio && status === SYNCING_STATUS.PROCESSING && event !== EVENT_NAME.DYNAMIC,
        ),
    );
  },
);

export const selectAspectRatioClipEventType = createSelector(
  selectClipDetailsByAspectRatio,
  (editedVideoObjectDeatilsByAspectRatio) => {
    return editedVideoObjectDeatilsByAspectRatio?.event;
  },
);

export const selectAspectRatioClipRequestId = createSelector(
  selectClipDetailsByAspectRatio,
  (editedVideoObjectDeatilsByAspectRatio) => {
    return editedVideoObjectDeatilsByAspectRatio?.requestId;
  },
);

export const selectIsClipsSyncedWithAspectRatio = createSelector(
  selectClipEditedVideosById,
  selectClipAspectRatioById,
  (editedVideos, aspectRatio) => {
    if (aspectRatio === ASPECT_RATIO["16_9"]) {
      return true;
    }

    return editedVideos.every(
      (editedVideos) =>
        editedVideos.length > 0 &&
        editedVideos.some(
          ({ aspect_ratio, status, event }) =>
            aspect_ratio === aspectRatio && status === SYNCING_STATUS.COMPLETED && event !== EVENT_NAME.DYNAMIC,
        ),
    );
  },
);

// Selector to check if a clip has metadata (tags)
export const selectIsClipHasMetadataOrTranscript = createSelector(selectClipDetailsById, (clip) => {
  if (!clip) return false;

  return isClipHasMetadata(clip);
});

export const selectIsClipIllustoTemplate = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.isIllustoTemplate,
);

export const selectIsClipCreateByIllusto = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.isClipCreateByIllusto,
);

export const selectIllustoClipStatusText = createSelector(
  selectIllustoClipStatus,
  selectIsClipCreateByIllusto,
  (state, props) => ({
    illustoExportProgress: props.illustoExportProgress,
  }),
  (illustoClipStatus, isClipCreateByIllusto, { illustoExportProgress }) => {
    if (!illustoClipStatus || !isClipCreateByIllusto) return "";
    if (illustoClipStatus === ILLUSTO_CLIP_STATUS.PENDING) {
      return illustoExportProgress > 0 ? "Exporting" : "Draft";
    }
    if (illustoClipStatus === ILLUSTO_CLIP_STATUS.COMPLETE) return "Exported";
    if (illustoClipStatus === ILLUSTO_CLIP_STATUS.FAILED) return "Failed";
  },
);

export const selectClipNamingTemplates = createSelector(selectClipState, (clipState) => clipState.clipNamingTemplates);

export const selectIsSameClipLanguage = createSelector(
  (_, props) => props.clipIds || [],
  selectClipEntities,
  (clipIds, clipEntities) =>
    clipIds.every((clipId) => {
      return clipEntities[clipId]?.clipLanguage === clipEntities[clipIds?.[0]]?.clipLanguage;
    }),
);

export const selectClipAspectioRatioBasedAllGeneratedThumbnailsById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.generatedThumbnailUrls || [],
);

export const selectClipAspectRatioBasedGeneratedThumbnailUrlsById = createSelector(
  selectClipAspectioRatioBasedAllGeneratedThumbnailsById,
  (_, props) => props.aspectRatio,
  (generatedThumbnailUrls, aspectRatio) =>
    generatedThumbnailUrls.find((thumbnail) => thumbnail?.aspectRatio === aspectRatio)?.thumbnails || [],
);

export const selectClipAspectRatioBasedVideoThumbnailUrlsById = createSelector(
  selectClipEditedVideosById,
  (_, props) => props.aspectRatio,
  (editedVideos, aspectRatio) =>
    editedVideos
      .filter(({ aspect_ratio, aspectRatio: videoAspectRatio }) =>
        [aspect_ratio, videoAspectRatio].includes(aspectRatio),
      )
      .flatMap(({ thumbnails }) => thumbnails || []),
);

export const selectCombinedClipVideoThumbnailUrlsById = createSelector(
  selectClipVideoThumbnailUrlsById,
  selectClipAspectRatioBasedVideoThumbnailUrlsById,
  selectClipAspectRatioBasedGeneratedThumbnailUrlsById,
  (_, props) => props.aspectRatio,
  (videoThumbnailUrls = [], aspectRatioBasedVideoThumbnails = [], generatedThumbnailUrls = [], aspectRatio) => {
    const combinedUrls =
      aspectRatio === ASPECT_RATIO["16_9"]
        ? [...(videoThumbnailUrls || []), ...(generatedThumbnailUrls || [])]
        : [...(aspectRatioBasedVideoThumbnails || []), ...(generatedThumbnailUrls || [])];

    return [...new Set(combinedUrls)].map((url) => ({
      url,
      isCustom: (generatedThumbnailUrls || []).includes(url),
    }));
  },
);

export const selectClipAudioTranslationLanguages = createSelector(() => {
  return AUDIO_TRANSLATION_LANGUAGES_OPTIONS;
});

export const selectClipsInQueueFilterModal = createSelector(
  selectClipModals,
  (modals) => modals.clipsInQueueFilterModal,
);

export const selectAllClipLanguages = createSelector(selectClipEntities, (clipEntities) =>
  Object.values(clipEntities).map((clip) => ({
    id: clip?._id,
    clipLanguage: clip?.clipLanguage || "",
  })),
);

export const selectClipLanguagesByIds = createSelector(
  selectAllClipLanguages,
  (_, props) => props.clipIds || [],
  (allClipLanguages, clipIds) => allClipLanguages.filter((clip) => clipIds.includes(clip.id)),
);

export const selectBookmarkedArchivalFiltersListCount = createSelector(selectClipState, (clipState) => {
  const bookmarkedArchivalFiltersList = clipState.archivalFiltersList.find(({ type }) => type === "bookmarked");
  const counts = {
    bookmarkedCount: 0,
    nonBookmarkedCount: 0,
  };

  if (!Array.isArray(bookmarkedArchivalFiltersList?.data)) return counts;

  bookmarkedArchivalFiltersList.data.forEach(({ key, count }) => {
    if (key === "true") {
      counts.bookmarkedCount = count;
    } else {
      counts.nonBookmarkedCount = count;
    }
  });

  return counts;
});

export const selectCloudPublishEventIds = createSelector(
  selectClipState,
  (clipState) => clipState.cloudPublishEventIds,
);

export const selectClipIsBookmarkedById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.isBookmarked,
);

export const selectClipThumbnailTemplateApplyModal = createSelector(
  selectClipModals,
  (modals) => modals.clipThumbnailTemplateApplyModal,
);

export const selectClipEntitiesByStreamId = createSelector(
  selectClipEntities,
  (_, props) => props.streamId,
  (clipEntities, streamId) => {
    return Object.values(clipEntities).filter((clip) => clip?.streamId === streamId);
  },
);

export const selectHLsSavedAsClipIdsByStreamId = createSelector(selectClipEntitiesByStreamId, (clipEntities) => {
  return Object.values(clipEntities)
    .filter((clip) => !clip?.retainedClip && clip?.isHighlightVideo && !clip?.isPromoVideo)
    .reduce((acc, clip) => {
      acc.push(clip._id);

      return acc;
    }, []);
});

export const selectHLsSavedAsClipCount = createSelector(selectClipState, (clipState) => clipState.hlsSavedAsClipCount);

export const selectIsAIClipSuggestionClip = createSelector(selectClipDetailsById, (clipDetails) =>
  Boolean(clipDetails?.ext_clip_suggest_id),
);

export const selectClipCutMarkersConfirmModal = createSelector(
  selectClipModals,
  (modals) => modals.clipCutMarkersConfirmModal,
);

export const selectClipFilterById = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.filter);

export const selectClipVariantVideoUrlById = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.variantUrl,
);

export const selectClipIsManualUpload = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.isManualUpload || false,
);
export const selectClipUploadedId = createSelector(
  selectClipDetailsById,
  (clipDetails) => clipDetails?.uploadedClipId || null,
);

export const selectUploadedClipDetailById = createSelector(
  selectClipState,
  (clipState) => clipState.uploadedClipDetail,
);

export const selectUploadedClipStartTimeById = createSelector(
  selectUploadedClipDetailById,
  (uploadedClipDetail) => uploadedClipDetail?.startTime || 0.001,
);

export const selectUploadedClipEndTimeById = createSelector(
  selectUploadedClipDetailById,
  (uploadedClipDetail) => uploadedClipDetail?.endTime || null,
);

export const selectUploadedClipvideoUrlById = createSelector(
  selectUploadedClipDetailById,
  (uploadedClipDetail) => uploadedClipDetail?.videoUrl || null,
);

export const selectClipCustomDataSportNameById = createSelector(
  selectClipCustomDataById,
  (customData) => customData?.sportName,
);

export const selectAnyClipHasNewsCategory = createSelector(
  selectClipEntities,
  (state) => state.stream?.entities || {},
  (clipEntities, streamEntities) => {
    return Object.values(clipEntities).some(
      (clip) =>
        clip?.customData?.sportName === NEWS_CATEGORY ||
        clip?.stream?.category === NEWS_CATEGORY ||
        streamEntities?.[clip?.streamId]?.category === NEWS_CATEGORY,
    );
  },
);

export const selectClipStreamById = createSelector(selectClipDetailsById, (clipDetails) => clipDetails?.stream);

export const selectClipStreamCategoryById = createSelector(selectClipStreamById, (clipStream) => clipStream?.category);

export const selectClipFinalCategoryById = createSelector(
  (state) => state.stream?.entities || {},
  (state, props) => props.clipDetails || null,
  selectClipDetailsById,
  (streamEntities, clipDetailsProps, clipDetails) => {
    const finalClipDetails = clipDetailsProps || clipDetails;
    const stream = streamEntities?.[finalClipDetails?.streamId];

    return finalClipDetails?.customData?.sportName || finalClipDetails?.stream?.category || stream?.category;
  },
);
